import { inject } from '@angular/core';
import { provideApollo } from 'apollo-angular';
import { InMemoryCache, ApolloLink } from '@apollo/client/core';
import { map } from 'rxjs';
import { createPersistedQueryLink } from 'apollo-angular/persisted-queries';
import { sha256 } from 'crypto-hash';
import ActionCableLink from 'graphql-ruby-client/subscriptions/ActionCableLink';
import { environment } from '../../environments/environment';
import { HttpLink } from './http-link';
import { inflate } from './inflate.js';
import { CableService } from '../core/services/cable/cable.service';
import PossibleTypesResultData from '../generated/introspection-result';

const hasSubscriptionOperation = ({ query: { definitions } }: ApolloLink.Operation): boolean =>
  definitions.some((def) => def.kind === 'OperationDefinition' && def.operation === 'subscription');

// Lazily initialise the cable so createCable() runs after login (token in localStorage), not at bootstrap.
function createLazySubscriptionLink(cableService: CableService): ApolloLink {
  let cableLink: ActionCableLink | undefined;
  return new ApolloLink((operation, forward) => {
    if (!cableLink) {
      cableLink = new ActionCableLink({ cable: cableService.createCable() });
    }

    return cableLink.request(operation, forward);
  });
}

function createPersistedHttpLink(httpLink: HttpLink, uri: string): ApolloLink {
  return createPersistedQueryLink({ sha256 }).concat(httpLink.create({ uri }));
}

// The Rails API deflates repeated objects in the response; inflate restores full object references.
function createInflateLink(): ApolloLink {
  return new ApolloLink((operation, forward) =>
    forward(operation).pipe(map((response) => inflate(response))),
  );
}

export function provideGraphQL() {
  return provideApollo(() => {
    const httpLink = inject(HttpLink);
    const cableService = inject(CableService);
    const uri = environment.apiBaseUrl + '/graphql';

    const subscriptionLink = createLazySubscriptionLink(cableService);
    const queryMutationLink = createPersistedHttpLink(httpLink, uri);
    const inflateLink = createInflateLink();
    const requestLink = ApolloLink.split(hasSubscriptionOperation, subscriptionLink, queryMutationLink);

    return {
      link: ApolloLink.from([inflateLink, requestLink]),
      cache: new InMemoryCache({ possibleTypes: PossibleTypesResultData.possibleTypes }),
      connectToDevTools: !environment.production,
      defaultOptions: {
        query: {
          fetchPolicy: 'cache-first',
        },
      },
    };
  });
}
